//== input listeners: DOM → state.
//== one-way wiring; render.js (later) handles state → DOM for derived
//== display fields. text inputs reformat their own value on blur here.
//== add new input bindings here as each step is wired.
//==
//== each bind* returns an optional reset function. initInputs collects them
//== and exposes resetInputs() for the start-over handler so DOM values and
//== radio-seeded state both return to "as just loaded" - store.reset alone
//== only clears state, leaving stale field values and pre-checked radios
//== out of sync with the cleared store.

import { parseMoney, formatMoney, parsePercent, formatPercent } from '../calc-shared/format.js'

export function initInputs(store, root) {
  const resetters = []
  const push = (fn) => { if (fn) resetters.push(fn) }

  push(bindProductRadios(store, root))
  push(bindDrawdownDate(store, root))
  push(bindBuildCompleteRadios(store, root))

  //== step 1: property details
  //== NB / THP use purchasePrice; SB uses buildCost + siteValue. all three
  //== are bound regardless of product so state survives switching - the
  //== inputs that aren't visible for the current product are hidden by
  //== render.js via [data-product]
  push(bindMoneyInput(store, root, '#txtPurchasePrice', 'purchasePrice'))
  push(bindMoneyInput(store, root, '#txtBuildCost', 'buildCost'))
  push(bindMoneyInput(store, root, '#txtSiteValue', 'siteValue'))
  push(bindMoneyInput(store, root, '#txtOriginalEquity', 'originalEquity'))
  push(bindPercentInput(store, root, '#txtCurrentSharePercentage', 'currentEquityPct'))

  //== step 2: current valuation
  push(bindMoneyInput(store, root, '#txtCurrentValuation', 'currentValuation'))

  //== step 3: redemption type + amount + service charges (full only)
  //== the redemption amount has two paired inputs - the range slider and
  //== a money text field. either can drive the value; the text field
  //== clamps typed input to the slider's current min / max on blur.
  push(bindRedemptionTypeRadios(store, root))
  push(bindRangeSlider(store, root, '#rangeAmount', 'amount'))
  push(bindRedemptionAmountInput(store, root, '#txtRangeOutput', '#rangeAmount'))
  push(bindMoneyInput(store, root, '#txtServiceCharges', 'serviceCharges'))

  function resetInputs() {
    resetters.forEach((fn) => fn())
  }

  return { resetInputs }
}

function bindProductRadios(store, root) {
  const radios = root.querySelectorAll('input[type="radio"][name="product_type"]')
  if (!radios.length) return null
  radios.forEach((radio) => {
    radio.addEventListener('change', (event) => {
      if (!event.target.checked) return
      store.setState({ product: event.target.value || null })
    })
  })

  //== seed (and re-seed on reset) from the HTML's defaultChecked radio so
  //== state matches the visible selection both on mount and after start-over
  function seedFromDefault() {
    let seed = null
    radios.forEach((r) => {
      r.checked = r.defaultChecked
      if (r.defaultChecked) seed = r
    })
    store.setState({ product: seed ? seed.value : null })
  }
  seedFromDefault()
  return seedFromDefault
}

function bindDrawdownDate(store, root) {
  let input = root.querySelector('#txtDrawnDate')
  if (!input) return null
  applyDrawdownBounds(input)
  const onChange = (event) => {
    store.setState({ drawdownDate: event.target.value || null })
  }
  input.addEventListener('change', onChange)
  return () => {
    input = replaceWithFreshClone(input)
    applyDrawdownBounds(input)
    input.addEventListener('change', onChange)
  }
}

//== constrain the native date picker: a drawdown can't be in the future, and
//== chromium's year spinner otherwise accepts up to 6 digits (e.g. 202645).
//== max is today; every 6-digit year is a future year, so this catches them.
//== out-of-range entries get the native :out-of-range flag and are rejected by
//== validateStep0's future check. no min floor is asserted for now.
function applyDrawdownBounds(input) {
  input.max = todayISO()
}

//== local-time yyyy-mm-dd for the max attribute; avoids toISOString()'s UTC
//== shift, which can roll the date back a day in negative-offset timezones
function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function bindMoneyInput(store, root, selector, key) {
  let input = root.querySelector(selector)
  if (!input) return null
  const onBlur = () => {
    const num = parseMoney(input.value)
    store.setState({ inputs: { [key]: num } })
    input.value = formatMoney(num)
  }
  input.addEventListener('blur', onBlur)
  return () => {
    input = replaceWithFreshClone(input)
    input.addEventListener('blur', onBlur)
  }
}

function bindPercentInput(store, root, selector, key) {
  let input = root.querySelector(selector)
  if (!input) return null
  const onBlur = () => {
    //== a locked field (render.js applyLocks) shows a derived value that must
    //== not be written back into state - state holds only entered values
    if (input.readOnly) return
    const num = parsePercent(input.value)
    store.setState({ inputs: { [key]: num } })
    input.value = formatPercent(num)
  }
  input.addEventListener('blur', onBlur)
  return () => {
    input = replaceWithFreshClone(input)
    input.addEventListener('blur', onBlur)
  }
}

//== swap a required text/date input for a parser-fresh element so the
//== browser's sticky "user-interacted" flag is dropped - otherwise
//== :user-invalid keeps matching after start-over even with the value
//== cleared. cloneNode preserves the flag in some engines (observed on
//== type=date and certain text inputs); re-parsing via <template> goes
//== through the HTML parser and guarantees fresh internal state.
//== returns the new node so callers can re-bind their listener.
function replaceWithFreshClone(input) {
  const tmpl = document.createElement('template')
  tmpl.innerHTML = input.outerHTML
  const fresh = tmpl.content.firstElementChild
  fresh.value = ''
  input.replaceWith(fresh)
  return fresh
}

function bindBuildCompleteRadios(store, root) {
  const radios = root.querySelectorAll('input[type="radio"][name="build_stage_type"]')
  if (!radios.length) return null
  radios.forEach((radio) => {
    radio.addEventListener('change', (event) => {
      if (!event.target.checked) return
      store.setState({ buildComplete: event.target.value === 'true' })
    })
  })
  function seedFromDefault() {
    let seed = null
    radios.forEach((r) => {
      r.checked = r.defaultChecked
      if (r.defaultChecked) seed = r
    })
    store.setState({ buildComplete: seed ? seed.value === 'true' : null })
  }
  seedFromDefault()
  return seedFromDefault
}

function bindRedemptionTypeRadios(store, root) {
  const radios = root.querySelectorAll('input[type="radio"][name="redempt_type"]')
  if (!radios.length) return null
  radios.forEach((radio) => {
    radio.addEventListener('change', (event) => {
      if (!event.target.checked) return
      store.setState({ redemption: { type: event.target.value || null } })
    })
  })
  function seedFromDefault() {
    let seed = null
    radios.forEach((r) => {
      r.checked = r.defaultChecked
      if (r.defaultChecked) seed = r
    })
    store.setState({ redemption: { type: seed ? seed.value : null } })
  }
  seedFromDefault()
  return seedFromDefault
}

function bindRangeSlider(store, root, selector, key) {
  const input = root.querySelector(selector)
  if (!input) return null
  input.addEventListener('input', () => {
    store.setState({ redemption: { [key]: Number(input.value) } })
  })
  //== no initial value - state stays null until the customer moves the slider
  //== or types an amount, so the paired text input and the summary total stay
  //== blank. render.js parks the thumb at the minimum in the meantime.
  return null
}

//== paired money input next to the range slider. typing + blur updates
//== state.redemption.amount, clamped to the slider's current min / max so
//== the user can't type a figure that the slider couldn't represent.
//== empty / unparseable input reverts to the current state value, so the
//== field stays blank only while no amount has been entered yet - once set,
//== it can't be cleared back to empty. the slider follows via the shared
//== state subscription, and render.js's data-show binding rewrites the
//== formatted value back into the field.
function bindRedemptionAmountInput(store, root, outputSelector, sliderSelector) {
  const slider = root.querySelector(sliderSelector)
  const output = root.querySelector(outputSelector)
  if (!slider || !output) return null

  output.addEventListener('blur', () => {
    const typed = parseMoney(output.value)
    const current = store.getState().redemption?.amount
    const min = slider.min ? Number(slider.min) : null
    const max = slider.max ? Number(slider.max) : null

    let next
    if (!Number.isFinite(typed)) {
      next = current
    } else if (Number.isFinite(min) && typed < min) {
      next = min
    } else if (Number.isFinite(max) && typed > max) {
      next = max
    } else {
      next = typed
    }

    if (Number.isFinite(next) && next !== current) {
      store.setState({ redemption: { amount: next } })
    } else {
      //== state didn't change so render.js won't fire - manually rewrite
      //== the formatted value to undo any typed-but-rejected text
      output.value = Number.isFinite(next) ? formatMoney(next) : ''
    }
  })

  return null
}
